import { LoginModel } from '@/models/DTO/authentication/login.model';
import { LoginResponseModel } from '@/models/DTO/authentication/login-response.model';
import { ForgotPasswordModel } from '@/models/DTO/authentication/forgot-password.model';
import { SetPasswordModel } from '@/models/DTO/authentication/set-password.model';
import { ResetPasswordModel } from '@/models/DTO/authentication/reset-password.model';
import { ChangePasswordModel } from '@/models/DTO/authentication/change-password.model';
import { RegisterAdminModel } from '@/models/DTO/authentication/register-admin.model';
import { RegisterVeterinarianModel } from '@/models/DTO/authentication/register-veterinarian.model';
import { Inject, injectable } from 'inversify-props';
import { RequestChangeEmailModel } from '@/models/DTO/authentication/request-change-email.model';
import { ConfirmChangeEmailModel } from '@/models/DTO/authentication/confirm-change-email.model';
import { BaseAPIService } from './base-api.service';
import { RegisterCustomerModel } from '@/models/DTO/customer/register-customer.model';
import { Customer } from '@/models/DTO/customer/customer.model';
import { CustomerLoginTokenRequestModel } from '@/models/DTO/authentication/customer-login-token-request.model';
import { ConfirmChangeEmailResponseModel } from '@/models/DTO/authentication/confirm-change-email-response.model';

const API_VERSION: string = process.env.VUE_APP_API_VERSION;
const ACCOUNT_URL_STUB = `/api/v${API_VERSION}/Account`;
const LOGIN_URL = `${ACCOUNT_URL_STUB}/Login`;
const CUSTOMER_LOGIN_URL = `${ACCOUNT_URL_STUB}/CustomerLogin`;
const CUSTOMER_LOGIN_TOKEN_REQUEST_URL = `${ACCOUNT_URL_STUB}/CustomerLoginTokenRequest`;
const LOGOUT_URL = `${ACCOUNT_URL_STUB}/Logout`;
const REGISTER_ADMIN_URL = `${ACCOUNT_URL_STUB}/RegisterAdmin`;
const REGISTER_VETERINARIAN_URL = `${ACCOUNT_URL_STUB}/RegisterVeterinarianUser`;
const FORGOT_PASSWORD_URL = `${ACCOUNT_URL_STUB}/ForgotPassword`;
const SET_PASSWORD_URL = `${ACCOUNT_URL_STUB}/SetPassword`;
const RESET_PASSWORD_URL = `${ACCOUNT_URL_STUB}/ResetPassword`;
const CHANGE_PASSWORD_URL = `${ACCOUNT_URL_STUB}/ChangePassword`;
export const REFRESH_TOKEN_URL = `${ACCOUNT_URL_STUB}/RefreshToken`;
const REVOKE_REFRESH_TOKEN_URL = `${ACCOUNT_URL_STUB}/RevokeRefreshToken`;
const REQUEST_CHANGE_EMAIL_URL = `${ACCOUNT_URL_STUB}/RequestChangeEmail`;
const CONFIRM_CHANGE_EMAIL_URL = `${ACCOUNT_URL_STUB}/ConfirmChangeEmail`;
const REGISTER_CUSTOMER_URL = `${ACCOUNT_URL_STUB}/RegisterCustomer`;
const REGISTER_CUSTOMER_IF_NEEDED_URL = `${ACCOUNT_URL_STUB}/RegisterCustomerIfNeeded`;
const SET_ACCEPTED_TERMS_KEY = `${ACCOUNT_URL_STUB}/SetAcceptedTermsKey`;

export interface AuthenticationService {
  setPassword(model: SetPasswordModel): Promise<void>;
  login(model: LoginModel): Promise<LoginResponseModel>;
  customerLoginTokenRequest(
    model: CustomerLoginTokenRequestModel
  ): Promise<void>;
  customerLogin(model: LoginModel): Promise<LoginResponseModel>;
  logout(): Promise<void>;
  refreshToken(): Promise<LoginResponseModel>;
  revokeToken(): Promise<void>;
  forgotPassword(model: ForgotPasswordModel): Promise<void>;
  resetPassword(model: ResetPasswordModel): Promise<void>;
  changePassword(model: ChangePasswordModel): Promise<void>;
  registerAdmin(model: RegisterAdminModel): Promise<void>;
  registerVeterinarian(model: RegisterVeterinarianModel): Promise<void>;
  registerCustomer(model: RegisterCustomerModel): Promise<Customer>;
  requestChangeEmail(model: RequestChangeEmailModel): Promise<void>;
  confirmChangeEmail(
    model: ConfirmChangeEmailModel
  ): Promise<ConfirmChangeEmailResponseModel>;
  setAcceptedTerms(): Promise<void>;
}

@injectable()
export class AuthenticationService implements AuthenticationService {
  @Inject()
  private readonly _baseAPIService!: BaseAPIService;

  public async login(model: LoginModel): Promise<LoginResponseModel> {
    const response = await this._baseAPIService.client.post<LoginResponseModel>(
      LOGIN_URL,
      {
        ...model
      }
    );

    this._baseAPIService.setJWT(response.data.token);

    return {
      ...response.data
    };
  }

  public async customerLoginTokenRequest(
    model: CustomerLoginTokenRequestModel
  ): Promise<void> {
    await this._baseAPIService.client.post(CUSTOMER_LOGIN_TOKEN_REQUEST_URL, {
      ...model
    });
  }

  public async customerLogin(model: LoginModel): Promise<LoginResponseModel> {
    const response = await this._baseAPIService.client.post<LoginResponseModel>(
      CUSTOMER_LOGIN_URL,
      {
        ...model
      }
    );

    this._baseAPIService.setJWT(response.data.token);

    return {
      ...response.data
    };
  }

  public async refreshToken(): Promise<LoginResponseModel> {
    const response = await this._baseAPIService.client.post<LoginResponseModel>(
      REFRESH_TOKEN_URL
    );

    this._baseAPIService.setJWT(response.data.token);

    return {
      ...response.data
    };
  }

  public async revokeToken(): Promise<void> {
    await this._baseAPIService.client.post(REVOKE_REFRESH_TOKEN_URL);
    this._baseAPIService.unsetJWT();
  }

  public async logout(): Promise<void> {
    await this._baseAPIService.client.post(LOGOUT_URL);
    this._baseAPIService.unsetJWT();
  }

  public async registerAdmin(model: RegisterAdminModel) {
    await this._baseAPIService.client.post(REGISTER_ADMIN_URL, {
      ...model
    });
  }

  public async registerVeterinarian(model: RegisterVeterinarianModel) {
    await this._baseAPIService.client.post(REGISTER_VETERINARIAN_URL, {
      ...model
    });
  }

  public async registerCustomer(
    model: RegisterCustomerModel
  ): Promise<Customer> {
    const resp = await this._baseAPIService.client.post(REGISTER_CUSTOMER_URL, {
      ...model
    });

    return resp.data;
  }

  public async registerCustomerIfNeeded(
    model: RegisterCustomerModel
  ): Promise<Customer> {
    const resp = await this._baseAPIService.client.post(
      REGISTER_CUSTOMER_IF_NEEDED_URL,
      {
        ...model
      }
    );

    return resp.data;
  }

  public async forgotPassword(model: ForgotPasswordModel): Promise<void> {
    await this._baseAPIService.client.post(FORGOT_PASSWORD_URL, {
      ...model
    });
  }

  /**
   * This sets a password without needing the original password.
   * The new autogenerated password will be emailed to the user's
   * email address on file.
   */
  public async setPassword(model: SetPasswordModel): Promise<void> {
    await this._baseAPIService.client.post(SET_PASSWORD_URL, {
      ...model
    });
  }

  /**
   * This is the second step in the forgot password functionality.
   */
  public async resetPassword(model: ResetPasswordModel): Promise<void> {
    await this._baseAPIService.client.post(RESET_PASSWORD_URL, {
      ...model
    });
  }

  public async changePassword(model: ChangePasswordModel): Promise<void> {
    await this._baseAPIService.client.post(CHANGE_PASSWORD_URL, {
      ...model
    });
  }

  public async requestChangeEmail(
    model: RequestChangeEmailModel
  ): Promise<void> {
    await this._baseAPIService.client.post(REQUEST_CHANGE_EMAIL_URL, {
      ...model
    });
  }

  public async confirmChangeEmail(
    model: ConfirmChangeEmailModel
  ): Promise<ConfirmChangeEmailResponseModel> {
    const resp = await this._baseAPIService.client.post<
      ConfirmChangeEmailResponseModel
    >(CONFIRM_CHANGE_EMAIL_URL, {
      ...model
    });

    return resp.data;
  }

  public async setAcceptedTerms(): Promise<void> {
    await this._baseAPIService.client.get(SET_ACCEPTED_TERMS_KEY);
  }
}
